import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Checkbox, ConfigProvider, Table, Tooltip, message, Popover, Empty, Tag, Dropdown, Modal } from "antd";
import { CopyOutlined, MoreOutlined } from "@ant-design/icons";
import ReactStars from "react-stars";
import { Link } from "react-router-dom";
import { EditInfo, ArchiveModal } from ".";
import { currencyConverter } from "../../utils/currency";
import { getCoursesList, bulkArchiveCourses, updateCourses } from "../../appRedux/actions/courses";
import { extractDomainAndFirstSegment } from "../../utils/courseUrl";

// Maps column key → API field name used by /v2/export?columns=...
const COLUMN_KEY_TO_FIELD = {
  4: "category",
  5: "affiliated",
  10: "website_url",
  14: "affiliate_url",
  7: "level",
  8: "language",
  9: "course_type",
  11: "duration",
  3: "rating",
  6: "mode",
  12: "price",
  15: "originalCourse_type",
  16: "originalAffiliation",
  17: "originalCategory",
};

function ListView({ onColumnsChange }) {
  const dispatch = useDispatch();
  const {
    courseList,
    queryInfo,
    totalRecords,
    selectedCurrency,
    currencyData,
    loader,
  } = useSelector((state) => ({
    courseList: state.courses.courseList,
    queryInfo: state.courses.queryInfo,
    totalRecords: state.courses.totalRecords,
    selectedCurrency: state.common.selectedCurrency,
    currencyData: state.common.currencyData,
    loader: state.common.loader,
  }));

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [courseList]);

  const handleBulkArchive = () => {
    Modal.confirm({
      title: "Archive Courses",
      content: `Are you sure you want to archive the ${selectedRowKeys.length} selected courses?`,
      okText: "Yes, Archive",
      okType: "danger",
      cancelText: "No",
      onOk() {
        return dispatch(bulkArchiveCourses({ ids: selectedRowKeys, queryInfo }));
      },
    });
  };

  const handleSitemapToggle = (record) => {
    dispatch(
      updateCourses({ courseId: record._id, sitemap_include: !record.sitemap_include }, queryInfo)
    ).then(() => {
      message.success(
        `Course ${!record.sitemap_include ? "included in" : "removed from"} sitemap`
      );
    }).catch(() => {
      message.error("Failed to update course sitemap status");
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
  };

  const handleTableChange = (pagination, filters, sorter) => {
    console.log({ pagination, filters, sorter });
    const { current, pageSize } = pagination;
    let sortBy = queryInfo["sort-by"] || "rating:desc";

    if (sorter && sorter.field && sorter.order) {
      const orderDir = sorter.order === "ascend" ? "asc" : "desc";
      const fieldMap = {
        title: "title",
        rating: "rating",
        price: "price",
      };
      const field = fieldMap[sorter.field] || sorter.field;
      sortBy = `${field}:${orderDir}`;
    }

    dispatch(
      getCoursesList({
        ...queryInfo,
        page: current,
        size: pageSize,
        "sort-by": sortBy,
      })
    );
  };
  const handleCopy = (website_url) => {
    const textToCopy = website_url;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        message.success("URL copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        message.error("Failed to copy URL");
      });
  };
  const columns = [
    {
      key: 1,
      title: "Title",
      dataIndex: "title",
      sorter: true,
      render: (title, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: "250px" }}>
          {record.image_url ? (
            <img 
              src={record.image_url} 
              alt={title} 
              style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "4px", border: "1px solid #eee" }} 
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div style={{ width: "48px", height: "48px", background: "#f0f2f5", borderRadius: "4px", border: "1px solid #eee" }} />
          )}
          <h2
            className="table-avatar m-0"
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            <Link to={`/course/${record._id}`}>{title}</Link>
          </h2>
        </div>
      ),
    },
    {
      key: 3,
      title: "Ratings",
      dataIndex: "rating",
      sorter: true,
      width: 140,
      render: (rating, record) => (
        <ReactStars
          count={5}
          value={
            rating && rating !== ""
              ? parseFloat(`${rating}`.replace(" stars", ""))
              : 0
          }
          edit={false}
          color2={"#ffd700"}
        />
      ),
    },
    {
      key: 2,
      title: "Platforms",
      dataIndex: "merchant",
      align: "center",
      render: (merchant, record) => (
        <Tag color="blue" style={{ borderRadius: '4px', textTransform: 'capitalize', fontWeight: '500', padding: '2px 8px' }}>
          {merchant}
        </Tag>
      ),
    },

    {
      key: 4,
      title: "Category",
      dataIndex: "category",
      align: "center",
      render: (category, record) => (
        <p className="px-3 py-2 m-0 text-capitalize">
          {category && category.name ? category.name : ""}{" "}
        </p>
      ),
    },
    {
      key: "instructor",
      title: "Instructor",
      dataIndex: "instructor",
      align: "center",
      render: (instructor, record) => (
        <p className="px-3 py-2 m-0 text-capitalize" style={{ whiteSpace: "nowrap" }}>
          {instructor || "-"}
        </p>
      ),
    },
    {
      key: 5,
      title: "Affiliation",
      dataIndex: "affiliation",
      render: (affiliation, record) => (
        <p className="px-3 py-2 m-0">{affiliation && affiliation.name} </p>
      ),
    },
    {
      key: 5,
      title: "Affiliated",
      dataIndex: "affiliated",
      align: "center",
      render: (affiliated, record) => {
        const isImpact = affiliated || record.affilated;
        return (
          <Tag color={isImpact ? "success" : "error"} style={{ borderRadius: '4px', fontWeight: '500', padding: '2px 8px' }}>
            {isImpact ? "Impact" : "Direct"}
          </Tag>
        );
      },
    },
    {
      key: 10,
      title: "Url",
      dataIndex: "website_url",
      align: "center",
      render: (website_url, record) => (
        <Tooltip placement="top" title="Click to copy full URL">
          <span
            className="url-copy-trigger"
            onClick={() => handleCopy(website_url)}
            style={{ cursor: "pointer", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px", color: "#1C75BC", fontWeight: "500", transition: "color 0.2s" }}
          >
            {extractDomainAndFirstSegment(website_url)}
            <CopyOutlined className="copy-icon" style={{ opacity: 0.6 }} />
          </span>
        </Tooltip>
      ),
    },
    {
      key: 14,
      title: "Referral Link",
      dataIndex: "affiliate_url",
      align: "center",
      render: (affiliate_url, record) => {
        const hasDeepLink = affiliate_url || record.affiliate_url;
        return (
          <Tag color={hasDeepLink ? "success" : "default"} style={{ borderRadius: '4px', fontWeight: '500', padding: '2px 8px' }}>
            {hasDeepLink ? "Deep Link" : "Not Available"}
          </Tag>
        );
      },
    },
    {
      key: 7,
      title: "Level",
      dataIndex: "level",
      align: "center",
      render: (level, record) => (
        <p className="px-3 py-2 m-0 text-capitalize">
          {level && level.name ? level.name : ""}{" "}
        </p>
      ),
    },
    {
      key: 8,
      title: "Language",
      dataIndex: "language",
      align: "center",
      render: (language, record) => (
        <p className="px-3 py-2 m-0 text-capitalize">{language} </p>
      ),
    },
    {
      key: 9,
      title: "Course Type",
      dataIndex: "course_type",
      align: "center",
      render: (course_type, record) => (
        <p className="px-3 py-2 m-0 text-capitalize">
          {course_type && course_type.name ? course_type.name : ""}{" "}
        </p>
      ),
    },

    {
      key: 11,
      title: "Course Duration",
      dataIndex: "duration",
      align: "center",
    },


    {
      key: 6,
      title: "Mode",
      dataIndex: "mode",
      align: "center",
      render: (mode, record) => (
        <p className="px-3 py-2 m-0 text-capitalize">
          {mode && mode.name ? mode.name : ""}{" "}
        </p>
      ),
    },
    {
      key: 12,
      title: "Price",
      dataIndex: "price",
      sorter: true,
      render: (affiliation, course) => {
        const price = currencyConverter(
          course.price,
          course.price_detail,
          selectedCurrency,
          currencyData
        );
        const discount = parseFloat() + parseFloat(price);
        currencyConverter(
          course.discount,
          course.price_detail,
          selectedCurrency,
          currencyData
        );

        return (
          <p className="px-3 py-2 m-0">
            {price && price !== "" && price !== "0" && price !== 0
              ? `${selectedCurrency.symbol}${price}`
              : "Free"}
            <p
              style={{
                color: "#AFAFAF",
                textDecoration: "line-through",
                textDecorationColor: "#AFAFAF",
              }}
            >
              {discount
                ? `${selectedCurrency.symbol}${discount.toFixed(2)}`
                : ""}
            </p>{" "}
          </p>
        );
      },
    },
    {
      key: 15,
      title: "Original Course Type",
      dataIndex: "originalCourse_type",
      align: "center",
      render: (course_type, record) => (
        <p className="px-3 py-2 m-0 text-capitalize">
          {course_type && course_type.name ? course_type.name : ""}{" "}
        </p>
      ),
    },
    {
      key: 16,
      title: "Original Affiliation",
      dataIndex: "originalAffiliation",
      render: (affiliation, record) => (
        <p className="px-3 py-2 m-0">{affiliation && affiliation.name} </p>
      ),
    },
    {
      key: 17,
      title: "Original Category",
      dataIndex: "originalCategory",
      align: "center",
      render: (category, record) => (
        <p className="px-3 py-2 m-0 text-capitalize">
          {category && category.name ? category.name : ""}{" "}
        </p>
      ),
    },
    {
      key: 13,
      title: "Action",
      align: "center",
      render: (text, record) => (
        <div className="text-center">
          <Dropdown
            menu={{
              items: [
                {
                  key: "edit",
                  label: <EditInfo info={record} />,
                },
                {
                  key: "archive",
                  label: <ArchiveModal info={record} />,
                },
                {
                  key: "sitemap",
                  label: (
                    <button
                      className="dropdown-item"
                      onClick={() => handleSitemapToggle(record)}
                      style={{ border: 'none', background: 'transparent', textAlign: 'left', width: '100%' }}
                    >
                      <i className={`fa ${record.sitemap_include ? 'fa-times' : 'fa-check'} m-r-5`} /> 
                      {record.sitemap_include ? "Remove from Sitemap" : "Include in Sitemap"}
                    </button>
                  ),
                },
              ]
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <button style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '6px', borderRadius: '6px', transition: 'background 0.2s' }} className="action-menu-btn">
              <MoreOutlined style={{ fontSize: '18px', color: '#6b7280' }} />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];
  const defaultCheckedList = columns.map((item) => item.key);
  const [checkedList, setCheckedList] = useState(defaultCheckedList);

  // Notify parent whenever visible columns change (for export)
  useEffect(() => {
    if (onColumnsChange) {
      const fields = checkedList
        .map((key) => COLUMN_KEY_TO_FIELD[key])
        .filter(Boolean);
      onColumnsChange(fields);
    }
  }, [checkedList]);
  const options = columns
    .map(({ key, title }) => {
      if (
        !(
          title === "Title" ||
          title === "Action" ||
          title === "Platforms" ||
          title === "Affiliation"
        )
      ) {
        return {
          label: title,
          value: key,
        };
      }
      return undefined;
    })
    .filter((option) => option !== undefined);

  const newColumns = columns.map((item) => {
    if (
      !(
        item.title === "Title" ||
        item.title === "Action" ||
        item.title === "Platforms" ||
        item.title === "Affiliation"
      )
    ) {
      return { ...item, hidden: !checkedList.includes(item.key) };
    } else {
      return { ...item, hidden: false };
    }
  });
  // const newColumns = columns.map((item) => ({
  //   ...item,
  //   hidden: !checkedList.includes(item.key),
  // }));

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return <a>Previous</a>;
    }
    if (type === "next") {
      return <a>Next</a>;
    }
    return originalElement;
  };
  const popoverContent = (
    <div style={{ maxHeight: "250px", overflowY: "auto", padding: "6px 12px" }}>
      <Checkbox.Group
        value={checkedList}
        onChange={(value) => {
          setCheckedList(value);
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {options.map((opt) => (
            <Checkbox key={opt.value} value={opt.value} className="text-capitalize">
              {opt.label}
            </Checkbox>
          ))}
        </div>
      </Checkbox.Group>
    </div>
  );

  return (
    <div className="row m-0">
      <div className="col-md-12 p-0" id="table-responsive">
        {/* Table Header Controls */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <h4 className="card-title mb-0" style={{ fontSize: "16px", fontWeight: "600", color: "#374151", marginRight: "16px" }}>
              Course Directory
            </h4>
          </div>
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            {selectedRowKeys.length > 0 && (
              <button
                onClick={handleBulkArchive}
                className="btn btn-outline-danger d-inline-flex align-items-center justify-content-center"
                style={{
                  height: "36px",
                  padding: "0 14px",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: "1px solid #ef4444",
                  color: "#ef4444",
                  background: "#fef2f2"
                }}
              >
                <i className="fa fa-archive" style={{ marginRight: "6px" }} /> Archive ({selectedRowKeys.length})
              </button>
            )}
            <Popover
            content={popoverContent}
            title={<strong style={{ fontSize: "14px", display: "block", borderBottom: "1px solid #f0f0f0", paddingBottom: "6px" }}>Toggle Column Visibility</strong>}
            trigger="click"
            placement="bottomRight"
            overlayStyle={{ width: "240px" }}
          >
            <button
              className="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center"
              style={{
                height: "36px",
                padding: "0 14px",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "500",
                border: "1px solid #d1d5db",
                background: "#ffffff",
                color: "#4b5563",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              <i className="fa fa-columns" style={{ marginRight: "6px" }} /> Column Settings
            </button>
          </Popover>
          </div>
        </div>

        <div className="table-responsive" id="setMinheight">
          {/* <ConfigProvider> */}
          <Table
            rowSelection={rowSelection}
            className="modern-course-table"
            loading={loader}
            scroll={{ x: "max-content" }}
            locale={{
              emptyText: <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>
                    No courses found matching your filters.
                  </span>
                }
              />
            }}
            pagination={{
              total: totalRecords,
              // defaultCurrent={6}
              current: queryInfo.page,
              pageSize: queryInfo.size || 5,
              pageSizeOptions: ["5", "10", "20", "50"],
              showSizeChanger: true,
              showTotal: (total, range) =>
                `Showing ${range[0]} to ${range[1]} of ${total} entries`,
              onShowSizeChange: (current, pageSize) => {
                console.log({ current, pageSize });
                dispatch(
                  getCoursesList({
                    ...queryInfo,
                    page: current,
                    size: pageSize,
                  })
                );
              }, //onShowSizeChange,
              itemRender: itemRender,
            }}
            onChange={(pagination, filters, sorter) =>
              handleTableChange(pagination, filters, sorter)
            }
            style={{ overflowX: "auto" }}
            columns={newColumns}
            // bordered
            dataSource={courseList}
            rowKey={(record) => record._id}
            // onChange={this.handleTableChange}
          />
          {/* </ConfigProvider> */}
        </div>
      </div>
    </div>
  );
}

export default ListView;
